import React from 'react';
import { Modal, Button, Alert, Table, Badge, Spinner } from 'react-bootstrap';

const InvoiceImpactModal = ({
  show,
  handleClose,
  handleConfirm,
  loading = false,
  targetStatus = 'completado',
  impacts = []
}) => {
  const hasMatchedInvoices = impacts.some(i => i.has_invoice);
  const totalAmount = impacts.reduce((acc, curr) => acc + (curr.amount || 0), 0);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(value || 0);
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered backdrop="static">
      <Modal.Header closeButton className={hasMatchedInvoices ? 'bg-primary-subtle' : 'bg-light'}>
        <Modal.Title className="fs-5 fw-bold d-flex align-items-center gap-2">
          {hasMatchedInvoices ? '📑 Sincronización con Facturación (Convenios)' : 'ℹ️ Confirmar Actualización de Estado'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2 text-muted">Verificando impacto en módulo de facturación...</p>
          </div>
        ) : (
          <div>
            {impacts.length === 1 ? (
              // Vista individual (1 transacción)
              <div>
                {impacts[0]?.has_invoice ? (
                  <Alert variant="info" className="d-flex align-items-start gap-2 shadow-sm">
                    <span className="fs-4">💡</span>
                    <div>
                      <div className="fw-bold mb-1">
                        Esta transacción completará la {impacts[0].doc_type} #{impacts[0].folio} del módulo de facturación.
                      </div>
                      <div className="small text-muted">
                        <strong>Cliente:</strong> {impacts[0].client_name} |{' '}
                        {impacts[0].pre_invoice_number && (
                          <>
                            <strong>Pre-Factura:</strong> {impacts[0].pre_invoice_number} |{' '}
                          </>
                        )}
                        <strong>Monto:</strong> {formatCurrency(impacts[0].amount)}
                      </div>
                    </div>
                  </Alert>
                ) : (
                  <Alert variant="secondary" className="d-flex align-items-start gap-2 shadow-sm">
                    <span className="fs-4">ℹ️</span>
                    <div>
                      <div className="fw-bold mb-1">
                        Esta transacción no está completando ningún registro en el módulo de facturación.
                      </div>
                      <div className="small text-muted">
                        Solo afectará al Flujo de Caja ({impacts[0]?.description || 'Transacción'} por {formatCurrency(impacts[0]?.amount)}).
                      </div>
                    </div>
                  </Alert>
                )}
              </div>
            ) : (
              // Vista en lote / masiva (> 1 transacción)
              <div>
                {hasMatchedInvoices ? (
                  <Alert variant="info" className="mb-3">
                    <div className="fw-bold">
                      Se detectaron {impacts.filter(i => i.has_invoice).length} de {impacts.length} transacciones vinculadas al módulo de facturación.
                    </div>
                    <div className="small">
                      Al marcarlas como <strong>{targetStatus}</strong>, se completarán automáticamente las facturas correspondientes en Convenios.
                    </div>
                  </Alert>
                ) : (
                  <Alert variant="secondary" className="mb-3">
                    <div className="fw-bold">
                      Ninguna de las {impacts.length} transacciones seleccionadas está vinculada a registros del módulo de facturación.
                    </div>
                    <div className="small">
                      Se actualizará únicamente su estado en el Flujo de Caja.
                    </div>
                  </Alert>
                )}

                <div className="table-responsive" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  <Table striped bordered hover size="sm" className="align-middle">
                    <thead className="table-light sticky-top">
                      <tr>
                        <th>Tx #</th>
                        <th>Descripción / Cliente</th>
                        <th>Monto</th>
                        <th>Facturación</th>
                      </tr>
                    </thead>
                    <tbody>
                      {impacts.map((item) => (
                        <tr key={item.transaction_id}>
                          <td><strong>#{item.transaction_id}</strong></td>
                          <td>
                            <div className="fw-semibold">{item.client_name}</div>
                            <div className="small text-muted text-truncate" style={{ maxWidth: '250px' }}>
                              {item.description}
                            </div>
                          </td>
                          <td>{formatCurrency(item.amount)}</td>
                          <td>
                            {item.has_invoice ? (
                              <Badge bg="success" className="px-2 py-1">
                                {item.doc_type} #{item.folio}
                              </Badge>
                            ) : (
                              <Badge bg="secondary" text="light" className="px-2 py-1">
                                Sin factura
                              </Badge>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
                <div className="text-end text-muted small mt-2">
                  Total seleccionado: <strong>{formatCurrency(totalAmount)}</strong>
                </div>
              </div>
            )}

            <div className="mt-3 p-2 bg-light rounded text-muted small border">
              ¿Estás seguro/a de cambiar el estado a <strong>{targetStatus}</strong>?
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={loading}>
          Cancelar
        </Button>
        <Button variant="success" onClick={handleConfirm} disabled={loading}>
          {hasMatchedInvoices ? 'Sí, Confirmar y Sincronizar' : 'Sí, Cambiar Estado'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default InvoiceImpactModal;
